import {
  ChangeDetectionStrategy,
  Component,
  Input,
  TemplateRef,
} from '@angular/core';
import { dynamicElementsGridBuilder } from '@epicuro-next/constants/dynamic-elements';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';

import { ColumnConfiguration, RowConfiguration } from '../interfaces';

const DEFAULT_ROW_HEADER_WIDTH = '10rem';
const DEFAULT_ROW_SUN_HEADER_WIDTH = '3rem';

@DynamicComponentRegister(dynamicElementsGridBuilder)
@Component({
  selector: 'atx-grid-builder',
  templateUrl: './grid-builder.component.html',
  styleUrls: ['./grid-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GridBuilderComponent {
  @Input() public gridTitle = '';
  @Input() public set rows(value: RowConfiguration<unknown>[]) {
    this.flattenedRows = this.getFlatenedRows(value);
    this.defaultRows = value;
  }
  @Input() public columns: ColumnConfiguration[] = [];
  // tdo: move header widths to configs
  @Input() public set rowHeaderConfig(value: {
    rowHeaderWidth?: string;
    rowSubHeaderWidth?: string;
    rowHeaderTemplate?: TemplateRef<unknown>;
    sticky?: boolean;
    subRowHeaderTemplate?: TemplateRef<unknown>;
  }) {
    this.rowHeaderWidth = value.rowHeaderWidth ?? DEFAULT_ROW_HEADER_WIDTH;
    this.rowSubHeaderWidth =
      value.rowSubHeaderWidth ?? DEFAULT_ROW_SUN_HEADER_WIDTH;
    if (value.rowHeaderTemplate) {
      this.rowHeaderTemplate = value.rowHeaderTemplate;
    }
    this.rowHeaderSticky = value.sticky ?? false;
    this.subRowHeaderTemplate = value.subRowHeaderTemplate;
  }

  @Input() public columnWidth = '1fr';
  @Input() public subHeaderColumnWidth = '1fr';
  @Input() public rowTemplate?: TemplateRef<unknown>;
  @Input() public cellTemplate?: TemplateRef<unknown>;
  @Input() public columnHeaderTemplate?: TemplateRef<unknown>;
  @Input() public gridCornerClass?: string;
  @Input() public stickyHeader = false;
  public subRowHeaderTemplate?: TemplateRef<unknown>;
  public rowHeaderTemplate?: TemplateRef<unknown>;
  public rowHeaderSticky = false;
  public rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH;
  public rowSubHeaderWidth = DEFAULT_ROW_SUN_HEADER_WIDTH;
  public flattenedRows: RowConfiguration<unknown>[] = [];
  public defaultRows: RowConfiguration<unknown>[] = [];

  get headerColumnConfig() {
    return this.defaultRows?.length || this.gridTitle
      ? `${this.firstColumnWidth} repeat(${this.columns.length}, ${this.columnWidth})`
      : `repeat(${this.columns.length}, ${this.columnWidth})`;
  }

  get firstColumnWidth() {
    const maxRowDepth = this.getDepth(this.defaultRows);
    return this.rowHeaderWidth === 'auto'
      ? 'auto'
      : `calc(${this.rowHeaderWidth} + ${this.rowSubHeaderWidth} * ${maxRowDepth})`;
  }

  private getDepth(rows: RowConfiguration<unknown>[], depth = 0): number {
    const subRows = rows.flatMap(({ childRows }) => childRows ?? []);
    if (subRows.length) {
      depth += 1;
      return this.getDepth(subRows, depth);
    } else {
      return depth;
    }
  }

  public getFlatenedRows(
    rows: RowConfiguration<unknown>[],
    totalRows: RowConfiguration<unknown>[] = [],
  ) {
    (rows || []).forEach((row: RowConfiguration<unknown>) => {
      if (row.childRows?.length) {
        this.getFlatenedRows(
          row.childRows.map((childRow) => ({ ...childRow, parentRow: row })),
          totalRows,
        );
      } else {
        totalRows.push(row);
      }
    });
    return totalRows;
  }

  public getCellClass(
    column: ColumnConfiguration,
    row: RowConfiguration<unknown>,
  ) {
    let cellClass = column.cellClass;
    if (row.cellClass) {
      cellClass += ` ${row.cellClass}`;
    }
    return cellClass;
  }
}
